import {
  useInstallIntegration,
  usePatchIntegration
} from "@/api/useIntegrations"
import { INTEGRATION_VENDORS } from "@/const/vendors-constants"
import { normalizeAcrLoginServer } from "@/pages/IntegrationsPage/helpers/acr"

import type {
  AcrInstallation,
  AcrInstallInput,
  AcrUpdateInput
} from "@/api/useIntegrations.types"
import type { ActionsIntegration } from "./useIntegration.types"

export interface AcrFormValues {
  azureTenantId?: string
  clientId?: string
  registry?: string
  repositories?: string[]
}

type AcrAPIProps = Omit<ActionsIntegration, "onRemove"> & {
  registry?: AcrInstallation
}

const MESSAGE_PREFIX = "integrations.acr.messages"

export const toAcrInstallInput = (values: AcrFormValues): AcrInstallInput => ({
  azureTenantId: values.azureTenantId?.trim().toLowerCase() || "",
  clientId: values.clientId?.trim().toLowerCase() || "",
  registries: [normalizeAcrLoginServer(values.registry || "")],
  repositories: values.repositories || []
})

// Unchanged identity fields are left out, so the stored ones are kept.
export const toAcrUpdateInput = (
  registry: AcrInstallation,
  values: AcrFormValues
): AcrUpdateInput => {
  const changed = (value: string | undefined, stored?: string | null) => {
    const next = value?.trim().toLowerCase()
    return next && next !== stored ? next : undefined
  }
  return {
    registryUrl: registry.registryUrl,
    repositories: values.repositories || [],
    azureTenantId: changed(values.azureTenantId, registry.azureTenantId),
    clientId: changed(values.clientId, registry.clientId)
  }
}

export const useAcrAPI = (props: AcrAPIProps) => {
  const { registry, t, message: messageApi } = props
  const vendor = INTEGRATION_VENDORS.acr
  const formId = registry?.registryUrl || `${vendor}-form`
  const installer = useInstallIntegration({ vendor })
  const updater = usePatchIntegration({ vendor })

  const onFinish = async (values: AcrFormValues) => {
    const prefix = `${MESSAGE_PREFIX}.${registry ? "update" : "add"}`
    try {
      if (registry) {
        await updater.mutateAsync({
          data: { acr: toAcrUpdateInput(registry, values) }
        })
      } else {
        await installer.mutateAsync({
          data: { acr: toAcrInstallInput(values) }
        })
      }
    } catch (error: any) {
      messageApi?.error(error?.data?.displayMessage || t(`${prefix}.failed`))
      return
    }

    messageApi?.success(t(`${prefix}.success`))
    if (registry) props.setIsDirty(false)
    else props.backToIntegration(true)
  }

  return {
    formId,
    onFinish,
    isPending: installer.isPending || updater.isPending
  }
}
